import type {
    OfflineError,
    OfflineScene,
    OfflineSceneKey,
    OfflineSceneState,
    OfflineViewState,
    View,
} from "@novorender/api";

import type { AppDispatch, RootState } from "app/store";
import type { OfflineDownloadStatusPayload } from "types/events";

import type { OfflineDownloadStatusUpdate } from "./hostEvents";
import { asOfflineError, legacySyncDates, sceneRecordFromState } from "./offlineRecord";
import { offlineActions } from "./offlineSlice";
import { trackOfflineFailure } from "./offlineTelemetry";

/** The open scene, and the store its actions report to. */
export type OfflineActionContext = {
    offlineState: OfflineViewState;
    view: View;
    sceneKey: OfflineSceneKey;
    sceneDirName: string;
    store: { getState(): RootState };
    dispatch: AppDispatch;
};

/**
 * Deletes a scene the widget lists, reporting to the host as it goes: the SDK's delete reports only
 * by resolving or rejecting. A sync of the scene in this tab is paused and settled first; another
 * tab's makes it reject `busy`. Never rejects: a failure leaves the scene listed, with the failure.
 * @param deletedSceneDirName By directory name: a record an older keying wrote has no key to recover.
 * @returns Whether the scene was deleted.
 */
export async function deleteOfflineScene(
    { offlineState, sceneDirName, store, dispatch }: OfflineActionContext,
    deletedSceneDirName: string,
    {
        emit,
        replaceDataAccess,
        lockHeld,
    }: {
        emit: (source: OfflineDownloadStatusPayload["source"], update: OfflineDownloadStatusUpdate) => void;
        replaceDataAccess: () => Promise<void>;
        lockHeld: () => Promise<void> | undefined;
    },
): Promise<boolean> {
    emit("delete", { status: "deleting" });
    dispatch(offlineActions.updateScene({ id: deletedSceneDirName, updates: { deleteFailure: undefined } }));
    try {
        // Only the open scene's files are read by the BIM index backend, and closing it is
        // process-wide.
        if (deletedSceneDirName === sceneDirName) {
            await replaceDataAccess();
            await lockHeld();
        }
        await offlineState.getSceneByDirName(deletedSceneDirName)?.delete();
    } catch (e) {
        void trackOfflineFailure({
            error: e,
            source: "offline.useHandleOffline.delete",
            severity: "error",
            scene: offlineState.getSceneByDirName(deletedSceneDirName),
        });
        // No `error` message: the viewer's copy is all download-flavoured, and wrong copy is worse
        // for a host than none.
        emit("delete", { status: "error", errorCode: "unknown" });
        dispatch(
            offlineActions.updateScene({ id: deletedSceneDirName, updates: { deleteFailure: asOfflineError(e) } }),
        );
        return false;
    }
    legacySyncDates.remove(deletedSceneDirName);
    dispatch(offlineActions.removeScene(deletedSceneDirName));
    if (store.getState().offline.estimate?.sceneId === deletedSceneDirName) {
        dispatch(offlineActions.setEstimate(undefined));
    }
    emit("delete", { status: "deleted" });
    return true;
}

/**
 * Deletes every scene on the device, one by one as {@link deleteOfflineScene} does but telling the
 * host nothing, then the directories no scene tracks and every {@link legacySyncDates} record. For
 * the debug utils.
 * @returns Whether everything was deleted. Stops at the first scene that fails, leaving it and the
 *   scenes after it listed.
 */
export async function deleteAllOfflineScenes(
    context: OfflineActionContext,
    options: Omit<Parameters<typeof deleteOfflineScene>[2], "emit">,
): Promise<boolean> {
    for (const scene of [...context.offlineState.iterateScenes()]) {
        if (!(await deleteOfflineScene(context, scene.id, { ...options, emit: () => {} }))) {
            return false;
        }
    }
    try {
        // Not `OfflineViewState.deleteAll`: it doesn't await the storage, so it resolves before
        // anything is deleted.
        await context.offlineState.storage.deleteAll();
    } catch (e) {
        void trackOfflineFailure({
            error: e,
            source: "offline.useHandleOffline.deleteAll",
            severity: "error",
            scene: undefined,
        });
        return false;
    }
    legacySyncDates.removeAll();
    return true;
}

/**
 * Enrols the open scene for incremental caching: `scene()` makes the directory the engine caches
 * into, and tracks it for eviction; a full sync's directory is left as it is. A scene whose index
 * names no offline manifest, or can't be read, gets no directory.
 * @param pruneEnrolled Tidies what the scene already holds once it is enrolled. Never rejects.
 */
export async function enrolIncremental(
    { offlineState, view, sceneKey, sceneDirName, store, dispatch }: OfflineActionContext,
    {
        signal,
        persistStorage,
        pruneEnrolled,
    }: {
        signal: AbortSignal;
        persistStorage: () => Promise<void>;
        pruneEnrolled: (scene: OfflineScene, state: OfflineSceneState) => Promise<void>;
    },
): Promise<void> {
    if (store.getState().offline.incrementalFailure) {
        dispatch(offlineActions.setIncrementalFailure(undefined));
    }

    let declared: boolean;
    try {
        declared = await declaresOfflineManifest(getSceneIndexUrl(view), signal);
    } catch (e) {
        if (!signal.aborted) {
            void trackOfflineFailure({
                error: e,
                source: "offline.useHandleOffline.enrolIncremental",
                scene: offlineState.getScene(sceneKey),
            });
            dispatch(offlineActions.setIncrementalFailure({ sceneId: sceneDirName, error: asOfflineError(e) }));
        }
        return;
    }
    if (!declared) {
        dispatch(offlineActions.addScene({ id: sceneDirName, view: { kind: "unsupported" } }));
        return;
    }

    let scene: OfflineScene;
    try {
        scene = await offlineState.scene(sceneKey);
    } catch (e) {
        void trackOfflineFailure({ error: e, source: "offline.useHandleOffline.enrolIncremental", scene: undefined });
        return;
    }
    await persistStorage();

    let state: OfflineSceneState;
    try {
        state = await scene.getState();
    } catch (e) {
        void trackOfflineFailure({ error: e, source: "offline.useHandleOffline.getState", scene });
        return;
    }
    // Enrolled with nothing cached yet reads "absent": the engine fills the directory as it loads.
    const record = sceneRecordFromState(
        scene.id,
        state.kind === "absent" ? { kind: "incremental", used: { bytes: 0, files: 0 } } : state,
        legacySyncDates.get(scene.id),
    );
    if (record) {
        dispatch(offlineActions.addScene(record));
    }
    await pruneEnrolled(scene, state);
}

/**
 * What a full sync of the open scene would download, for the widget to show. By the scene's key,
 * not through `scene()`: a scene the user doesn't download is left as it was, not enrolled for
 * incremental caching.
 */
export async function estimateFullSync(
    { offlineState, view, sceneKey, sceneDirName, dispatch }: OfflineActionContext,
    signal: AbortSignal,
): Promise<void> {
    try {
        const estimate = await offlineState.estimate(sceneKey, getSceneIndexUrl(view), signal);
        if (signal.aborted) {
            return;
        }
        dispatch(
            offlineActions.setEstimate({
                kind: "ready",
                sceneId: sceneDirName,
                total: estimate.total,
            }),
        );
    } catch (e) {
        if (signal.aborted) {
            return;
        }
        void trackOfflineFailure({
            error: e,
            source: "offline.useHandleOffline.estimate",
            scene: offlineState.getScene(sceneKey),
        });
        dispatch(offlineActions.setEstimate({ kind: "failed", sceneId: sceneDirName, error: asOfflineError(e) }));
    }
}

/** Asks the browser to keep the origin's storage under pressure. */
export async function persistStorage(): Promise<void> {
    if (!(await navigator.storage.persisted())) {
        await navigator.storage.persist();
    }
}

export function getSceneIndexUrl(view: View): URL {
    const url = view.renderState.scene?.url ? new URL(view.renderState.scene.url) : undefined;

    if (!url) {
        throw new Error("Unable to parse url for SAS key");
    }

    // Procore FAS names the file in a query parameter rather than the path.
    if (url.pathname.includes("/versions/")) {
        url.searchParams.set("file", "index.json");
    } else {
        url.pathname += "index.json";
    }

    return url;
}

/**
 * Whether the scene index names an offline manifest, as every scene the pipeline makes ready for
 * download does.
 * @throws An {@link OfflineError} when the scene index can't be had or read; the abort reason if
 *   `signal` aborts.
 */
async function declaresOfflineManifest(sceneIndexUrl: URL, signal: AbortSignal): Promise<boolean> {
    const path = "index.json";
    let response: Response;
    try {
        response = await fetch(sceneIndexUrl, { signal });
    } catch (e) {
        signal.throwIfAborted();
        throw {
            kind: navigator.onLine ? "downloadFailed" : "offline",
            message: `The scene index got no answer: ${e instanceof Error ? e.message : String(e)}`,
            path,
        } satisfies OfflineError;
    }
    if (!response.ok) {
        throw {
            kind: "downloadFailed",
            message: `The scene index answered ${response.status}`,
            path,
            httpStatus: response.status,
        } satisfies OfflineError;
    }
    let sceneIndex: unknown;
    try {
        sceneIndex = await response.json();
    } catch {
        signal.throwIfAborted();
        throw { kind: "invalidManifest", message: "The scene index isn't JSON", path } satisfies OfflineError;
    }
    const manifest = (sceneIndex as { offline?: { manifest?: unknown } } | null)?.offline?.manifest;
    return typeof manifest === "string" && manifest !== "";
}
